import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import { PageProps } from '../../../types'
import Layout from '../../../components/Layout'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'

interface SvepPlanData {
  id: number
  team_id: number
  quarter: string
  annual_data: Record<string, any>
  quarterly_data: Record<string, any>
  created_at: string
  updated_at: string
}

interface Props extends PageProps {
  team: { id: number; name: string; description: string | null }
  active_tab: string
  plan: SvepPlanData
  can_edit: boolean
  current_quarter: string
  available_quarters: string[]
  prev_quarter: string
  next_quarter: string
  team_members: { id: number; full_name: string }[]
  // Task #701: the team's qualifying Rock records for the viewed quarter,
  // already filtered and ordered by position server-side.
  team_rocks?: { id: number; title: string; owner_name: string }[]
}

function SvepField({ value, onChange, placeholder, multiline, className, readOnly }: {
  value: string; onChange: (v: string) => void; placeholder?: string; multiline?: boolean; className?: string; readOnly?: boolean
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  // Auto-size the textarea to its content so wrapped lines aren't clipped by
  // the surrounding cell. We snap to the scroll height after every change.
  useEffect(() => {
    if (!multiline || !textareaRef.current) return
    const el = textareaRef.current
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value, multiline])

  if (multiline) {
    return (
      <textarea
        ref={textareaRef}
        value={value}
        onChange={e => { if (!readOnly) onChange(e.target.value) }}
        readOnly={readOnly}
        placeholder={placeholder}
        rows={1}
        className={`svep-f w-full resize-none border-0 bg-transparent focus:outline-none p-[8px] ${className || ''}`}
      />
    )
  }
  return (
    <input
      type="text"
      value={value}
      onChange={e => { if (!readOnly) onChange(e.target.value) }}
      readOnly={readOnly}
      placeholder={placeholder}
      className={`svep-f w-full border-0 bg-transparent focus:outline-none px-[8px] py-[3px] ${className || ''}`}
    />
  )
}

// Dense Rocks keep a fixed row box; only their text shrinks, not other SVEP fields.
function DenseRockField({ value, onChange, count, readOnly }: {
  value: string; onChange: (value: string) => void; count: number; readOnly?: boolean
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const fit = useCallback(() => {
    const el = ref.current
    if (!el || !el.clientHeight) return
    let size = 13
    el.style.fontSize = `${size}px`
    while (size > 9 && (el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth)) {
      size -= 0.25
      el.style.fontSize = `${size}px`
    }
  }, [])
  useLayoutEffect(fit, [fit, value, count])
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    document.fonts.ready.then(fit)
    return () => observer.disconnect()
  }, [fit])
  return <textarea ref={ref} className="svep-f svep-rock-field" rows={1}
    value={value} readOnly={readOnly}
    onChange={e => { if (!readOnly) onChange(e.target.value) }} />
}

function LinkedRockField({ value, owner, onChange, count, readOnly }: {
  value: string; owner: string; onChange: (value: string) => void; count: number; readOnly: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const fit = useCallback(() => {
    ref.current?.querySelectorAll<HTMLElement>('textarea, .svep-rock-owner').forEach(el => {
      if (!el.clientHeight) return
      let size = 13
      el.style.fontSize = `${size}px`
      while (size > 9 && (el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth)) {
        size -= 0.25
        el.style.fontSize = `${size}px`
      }
    })
  }, [])
  useLayoutEffect(fit, [fit, value, owner, count])
  useEffect(() => {
    if (!ref.current) return
    const observer = new ResizeObserver(fit)
    observer.observe(ref.current)
    document.fonts.ready.then(fit)
    return () => observer.disconnect()
  }, [fit])
  return (
    <div ref={ref} className="svep-linked-rock-content">
      <textarea className="svep-f svep-rock-field" rows={1} value={value}
        aria-label="SVEP Rock title" readOnly={readOnly}
        onChange={e => { if (!readOnly) onChange(e.target.value) }} />
      <span className="svep-rock-owner" title={owner}>— {owner}</span>
    </div>
  )
}

function NumberedCircle({ n, color = '#ED1C24' }: { n: number; color?: string }) {
  return (
    <svg className="svep-circle flex-shrink-0" width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="10" fill={color} />
      <text
        x="11"
        y="11"
        textAnchor="middle"
        dominantBaseline="central"
        fill="#ffffff"
        fontSize="11"
        fontWeight="700"
        fontFamily="Montserrat, sans-serif"
      >
        {n}
      </text>
    </svg>
  )
}
function RedCircle({ n }: { n: number }) {
  return <NumberedCircle n={n} color="#ED1C24" />
}
function DarkCircle({ n }: { n: number }) {
  return <NumberedCircle n={n} color="#272524" />
}

function RedBullet() {
  return <span className="svep-bullet inline-block rounded-full bg-[#ED1C24] flex-shrink-0" />
}

function CountStepper({ label, value, min, max, onChange }: {
  label: string; value: number; min: number; max: number; onChange: (next: number) => void
}) {
  return (
    <div className="svep-stepper print:hidden">
      <span className="svep-stepper-label">{label}</span>
      <button type="button" onClick={() => onChange(value - 1)} disabled={value <= min} aria-label={`Fewer ${label}`}>−</button>
      <span className="svep-stepper-n">{value}</span>
      <button type="button" onClick={() => onChange(value + 1)} disabled={value >= max} aria-label={`More ${label}`}>+</button>
    </div>
  )
}

function SvepImageField({ value, textValue, onImageChange, onTextChange, placeholder, className }: {
  value: string | null
  textValue: string
  onImageChange: (dataUrl: string | null) => void
  onTextChange: (v: string) => void
  placeholder?: string
  className?: string
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const handleFile = (file: File | null) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => onImageChange(typeof reader.result === 'string' ? reader.result : null)
    reader.readAsDataURL(file)
  }
  if (value) {
    return (
      <div className={`svep-img-cell ${className || ''}`}>
        <img src={value} alt="" className="svep-img-content" />
        <button type="button" className="svep-img-remove print:hidden" onClick={() => onImageChange(null)} aria-label="Remove image">×</button>
      </div>
    )
  }
  return (
    <div className={`svep-img-cell svep-img-empty ${className || ''}`}>
      <textarea
        value={textValue}
        onChange={e => onTextChange(e.target.value)}
        placeholder={placeholder}
        className="svep-f w-full h-full resize-none border-0 bg-transparent focus:outline-none p-[8px]"
      />
      <button
        type="button"
        className="svep-img-upload print:hidden"
        onClick={() => inputRef.current?.click()}
      >
        Upload image
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => handleFile(e.target.files?.[0] || null)}
      />
    </div>
  )
}

interface FlywheelBuilderData {
  step_count: number
  steps: string[]
  color: string
  logo_image: string | null
}

const FLYWHEEL_PALETTE = [
  '#ED1C24', // Pinnacle red
  '#C41920', // Pinnacle red dark
  '#351E1D', // Charcoal
  '#1F3A5F', // Navy
  '#2D6A4F', // Forest green
  '#B8860B', // Mustard
  '#5B4B8A', // Plum
]

function resizeSteps(steps: string[], count: number): string[] {
  const next = (steps || []).slice(0, count)
  while (next.length < count) next.push('')
  return next
}

function defaultBuilder(): FlywheelBuilderData {
  return { step_count: 5, steps: ['', '', '', '', ''], color: '#ED1C24', logo_image: null }
}

function SvepFlywheel({ builder, onChange, editable = true }: {
  builder: FlywheelBuilderData
  onChange: (next: FlywheelBuilderData) => void
  editable?: boolean
}) {
  const logoInputRef = useRef<HTMLInputElement | null>(null)
  const n = Math.max(4, Math.min(7, builder.step_count || 5))
  const stepsArr = resizeSteps(builder.steps || [], n)
  const color = builder.color || '#ED1C24'
  const logo = builder.logo_image || null

  // viewBox 0..100
  const cx = 50, cy = 50
  const ringR = 34
  const stepR = n === 4 ? 15 : n === 5 ? 13.5 : n === 6 ? 12 : 11
  const centerR = 13
  const arrowGap = stepR + 1

  const positions = stepsArr.map((_, i) => {
    const a = (i * 2 * Math.PI / n) - Math.PI / 2
    return { x: cx + ringR * Math.cos(a), y: cy + ringR * Math.sin(a), a }
  })
  const arrows = positions.map((p, i) => {
    const next = positions[(i + 1) % n]
    const delta = arrowGap / ringR
    const sa = p.a + delta
    const ea = next.a - delta
    return {
      sx: cx + ringR * Math.cos(sa), sy: cy + ringR * Math.sin(sa),
      ex: cx + ringR * Math.cos(ea), ey: cy + ringR * Math.sin(ea),
    }
  })
  const markerId = `fw-arrow-${color.replace('#', '')}`

  const handleLogoFile = (file: File | null) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => onChange({ ...builder, step_count: n, steps: stepsArr, logo_image: typeof reader.result === 'string' ? reader.result : null })
    reader.readAsDataURL(file)
  }

  return (
    <div className="svep-fw-inner">
      <svg className="svep-fw-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id={markerId} viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto">
            <path d="M0,0 L10,5 L0,10 Z" fill={color} />
          </marker>
        </defs>
        {arrows.map((ar, i) => (
          <path
            key={i}
            d={`M ${ar.sx} ${ar.sy} A ${ringR} ${ringR} 0 0 1 ${ar.ex} ${ar.ey}`}
            stroke={color}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            markerEnd={`url(#${markerId})`}
          />
        ))}
      </svg>
      <div
        className="svep-fw-center"
        style={{
          left: `${cx - centerR}%`,
          top: `${cy - centerR}%`,
          width: `${centerR * 2}%`,
          height: `${centerR * 2}%`,
        }}
      >
        {logo ? (
          <>
            <img src={logo} alt="" />
            {editable && (
              <button
                type="button"
                className="svep-fw-logo-remove print:hidden"
                onClick={() => onChange({ ...builder, step_count: n, steps: stepsArr, logo_image: null })}
                aria-label="Remove logo"
              >×</button>
            )}
          </>
        ) : editable ? (
          <>
            <button
              type="button"
              className="svep-fw-logo-add print:hidden"
              onClick={() => logoInputRef.current?.click()}
            >+ Logo</button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => handleLogoFile(e.target.files?.[0] || null)}
            />
          </>
        ) : null}
      </div>
      {positions.map((p, i) => (
        <div
          key={i}
          className="svep-fw-step"
          style={{
            left: `${p.x - stepR}%`,
            top: `${p.y - stepR}%`,
            width: `${stepR * 2}%`,
            height: `${stepR * 2}%`,
            background: color,
          }}
        >
          <FlywheelStepLabel
            value={stepsArr[i]}
            onChange={v => {
              const next = stepsArr.slice()
              next[i] = v
              onChange({ ...builder, step_count: n, steps: next })
            }}
            editable={editable}
            placeholder={`Step ${i + 1}`}
            stepCount={n}
          />
        </div>
      ))}
    </div>
  )
}

function FlywheelStepLabel({ value, onChange, editable, placeholder, stepCount }: {
  value: string
  onChange: (v: string) => void
  editable: boolean
  placeholder?: string
  stepCount: number
}) {
  const taRef = useRef<HTMLTextAreaElement>(null)
  const MAX_SIZE = 10
  const MIN_SIZE = 5

  const fit = useCallback(() => {
    const ta = taRef.current
    if (!ta) return
    const parent = ta.parentElement
    if (!parent) return
    const maxH = parent.clientHeight * 0.82
    let size = MAX_SIZE
    ta.style.fontSize = `${size}px`
    ta.style.height = 'auto'
    ta.style.height = `${ta.scrollHeight}px`
    let guard = 0
    while (size > MIN_SIZE && ta.scrollHeight > maxH && guard < 40) {
      size -= 0.5
      ta.style.fontSize = `${size}px`
      ta.style.height = 'auto'
      ta.style.height = `${ta.scrollHeight}px`
      guard += 1
    }
  }, [])

  useLayoutEffect(() => {
    fit()
  }, [value, stepCount, placeholder, fit])

  useEffect(() => {
    const ta = taRef.current
    if (!ta || !ta.parentElement) return
    const ro = new ResizeObserver(() => fit())
    ro.observe(ta.parentElement)
    return () => ro.disconnect()
  }, [fit])

  return (
    <textarea
      ref={taRef}
      className="svep-f svep-fw-label"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={!editable}
      rows={1}
      spellCheck={false}
    />
  )
}

function SvepFlywheelField({ imageValue, builderValue, textValue, onImageChange, onBuilderChange, onTextChange, placeholder, className, headerTitle }: {
  imageValue: string | null
  builderValue: FlywheelBuilderData | null
  textValue: string
  onImageChange: (dataUrl: string | null) => void
  onBuilderChange: (next: FlywheelBuilderData | null) => void
  onTextChange: (v: string) => void
  placeholder?: string
  className?: string
  headerTitle: string
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleUploadedImage = (file: File | null) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : null
      if (!url) return
      if (builderValue) {
        if (!window.confirm('Switching to an uploaded image will discard the built flywheel. Continue?')) return
        onBuilderChange(null)
      }
      onImageChange(url)
    }
    reader.readAsDataURL(file)
  }

  const startBuilder = () => {
    if (imageValue) {
      if (!window.confirm('Switching to the Flywheel Builder will discard the uploaded image. Continue?')) return
      onImageChange(null)
    }
    onBuilderChange(defaultBuilder())
  }

  const removeBuilder = () => {
    if (!window.confirm('Remove the built flywheel?')) return
    onBuilderChange(null)
  }

  if (imageValue) {
    return (
      <>
        <div className="svep-flywheel-hdr">
          <strong>{headerTitle}</strong>
          <div className="svep-flywheel-hdr-actions print:hidden">
            <button type="button" className="svep-fw-chooser-btn" onClick={startBuilder}>Build flywheel</button>
            <button type="button" className="svep-fw-chooser-btn" onClick={() => onImageChange(null)} aria-label="Remove image">Remove image</button>
          </div>
        </div>
        <div className="svep-flywheel-body">
          <div className={`svep-img-cell ${className || ''}`}>
            <img src={imageValue} alt="" className="svep-img-content" />
            <button
              type="button"
              className="svep-img-remove print:hidden"
              onClick={() => onImageChange(null)}
              aria-label="Remove image"
            >×</button>
          </div>
        </div>
      </>
    )
  }

  if (builderValue) {
    const n = Math.max(4, Math.min(7, builderValue.step_count || 5))
    const setCount = (next: number) => {
      const c = Math.max(4, Math.min(7, next))
      onBuilderChange({
        ...builderValue,
        step_count: c,
        steps: resizeSteps(builderValue.steps || [], c),
      })
    }
    return (
      <>
        <div className="svep-flywheel-hdr">
          <strong>{headerTitle}</strong>
        </div>
        <div className="svep-flywheel-body">
      <div className={`svep-fw-cell ${className || ''}`}>
        <div className="svep-fw-controls print:hidden">
          <div className="svep-fw-stepper">
            <span>Steps</span>
            <button type="button" onClick={() => setCount(n - 1)} disabled={n <= 4} aria-label="Fewer steps">−</button>
            <span className="svep-fw-stepper-n">{n}</span>
            <button type="button" onClick={() => setCount(n + 1)} disabled={n >= 7} aria-label="More steps">+</button>
          </div>
          <div className="svep-fw-palette" role="group" aria-label="Flywheel color">
            {FLYWHEEL_PALETTE.map(c => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                className={`svep-fw-swatch ${builderValue.color === c ? 'svep-fw-swatch-active' : ''}`}
                style={{ background: c }}
                onClick={() => onBuilderChange({ ...builderValue, color: c })}
              />
            ))}
          </div>
          <div className="svep-fw-mode-actions">
            <button type="button" className="svep-fw-mode-btn" onClick={() => inputRef.current?.click()}>Upload image</button>
            <button type="button" className="svep-fw-clear" onClick={removeBuilder}>Remove flywheel</button>
          </div>
        </div>
        <div className="svep-fw-body">
          <SvepFlywheel builder={builderValue} onChange={onBuilderChange} />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => handleUploadedImage(e.target.files?.[0] || null)}
        />
      </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="svep-flywheel-hdr">
        <strong>{headerTitle}</strong>
      </div>
      <div className="svep-flywheel-body">
        <div className={`svep-img-cell svep-img-empty svep-fw-empty ${className || ''}`}>
          <textarea
            value={textValue}
            onChange={e => onTextChange(e.target.value)}
            placeholder={placeholder}
            className="svep-f w-full h-full resize-none border-0 bg-transparent focus:outline-none p-[8px]"
          />
          <div className="svep-fw-chooser print:hidden">
            <button type="button" className="svep-fw-chooser-btn" onClick={() => inputRef.current?.click()}>Upload image</button>
            <button type="button" className="svep-fw-chooser-btn" onClick={startBuilder}>Build flywheel</button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => handleUploadedImage(e.target.files?.[0] || null)}
          />
        </div>
      </div>
    </>
  )
}

function SvepHeader() {
  return (
    <div className="svep-hdr">
      <div className="svep-hdr-bar">
        <h2 className="svep-hdr-title">The Strategic Vision & Execution Plan</h2>
        <div className="svep-hdr-mtn">
          <img src="/images/svep-mountain.png" alt="" className="svep-hdr-mtn-img" />
        </div>
      </div>
      <div className="svep-hdr-redline" />
    </div>
  )
}

function PinnacleFooter({ withText }: { withText?: boolean }) {
  return (
    <div className={`svep-footer ${withText ? 'svep-footer-full' : ''}`}>
      <span className="svep-footer-copy">{withText ? `©${new Date().getFullYear()} Pinnacle Business Guides` : ''}</span>
      <div className="svep-footer-center">
        <div className="svep-footer-logo">
          <img src="/pinnacle-logo.png" alt="Pinnacle" className="svep-footer-img" />
          {withText && <span className="svep-footer-text">Pinnacle</span>}
        </div>
      </div>
      <span />
    </div>
  )
}

export default function SvepShow({
  team, active_tab, plan, current_quarter, available_quarters,
  prev_quarter, next_quarter, team_members, team_rocks = [], can_edit
}: Props) {
  const [annual, setAnnual] = useState<Record<string, any>>(plan.annual_data || {})
  const [quarterly, setQuarterly] = useState<Record<string, any>>(plan.quarterly_data || {})
  const quarterlyRef = useRef(quarterly)
  quarterlyRef.current = quarterly
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveInFlightRef = useRef(0)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setAnnual(plan.annual_data || {})
    setQuarterly(plan.quarterly_data || {})
    setLastSaved(null)
    setSaveError(null)
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [plan.id, plan.quarter])

  useEffect(() => {
    const removeInvalid = router.on('invalid', (event: any) => {
      if (saveInFlightRef.current > 0) {
        event.preventDefault()
        saveInFlightRef.current = 0
        setSaving(false)
        setSessionExpired(true)
        setSaveError('Your session expired — please refresh the page and sign in again.')
      }
    })
    const removeException = router.on('exception', (event: any) => {
      if (saveInFlightRef.current > 0) {
        event.preventDefault()
        saveInFlightRef.current = 0
        setSaving(false)
        setSaveError('Connection error. Changes not saved.')
        setTimeout(() => setSaveError(prev => (prev === 'Connection error. Changes not saved.' ? null : prev)), 8000)
      }
    })
    return () => {
      removeInvalid()
      removeException()
    }
  }, [])

  const exportPdf = useCallback(async () => {
    setExporting(true)
    setExportError(null)
    try {
      const page1 = document.getElementById('svep-page-1')
      const page2 = document.getElementById('svep-page-2')
      if (!page1 || !page2) return

      const container = document.getElementById('svep-print-area')
      if (!container) return

      // html2canvas draws text ~10px lower than the browser; this class shifts
      // the section-header text up so it lands centred in the rasterised bands
      // (see .svep-exporting in application.css). Removed again in cleanup.
      container.classList.add('svep-exporting')

      // Hide editor chrome (toolbar controls, upload/remove buttons) during
      // capture — html2canvas ignores @media print, so print:hidden alone
      // isn't enough.
      const chromeEls = container.querySelectorAll<HTMLElement>(
        '.svep-fw-controls, .svep-fw-logo-add, .svep-fw-logo-remove, .svep-fw-chooser, .svep-flywheel-hdr-actions, .svep-stepper, .svep-img-upload, .svep-img-remove'
      )
      const chromeReplacements: { el: HTMLElement; prevDisplay: string }[] = []
      chromeEls.forEach(el => {
        chromeReplacements.push({ el, prevDisplay: el.style.display })
        el.style.display = 'none'
      })

      // Replace inputs with divs in-place so html2canvas captures real text.
      // Read-only Rock rows (task #701) are rendered with the same fields as
      // the free-text rows, so they are picked up here too and inherit the
      // same height, dense-mode and shrunk-font handling.
      const inputs = container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
      const replacements: { input: HTMLElement; div: HTMLElement; prevDisplay: string }[] = []
      inputs.forEach(input => {
        const val = input.value
        const cs = window.getComputedStyle(input)
        const isTextarea = input.tagName === 'TEXTAREA'
        const isFwLabel = input.classList.contains('svep-fw-label')
        const isDenseRock = input.classList.contains('svep-rock-field')
        const div = document.createElement('div')
        div.className = `${input.className} svep-pdf-text`
        div.textContent = val
        // Flywheel step labels live inside a flex-centered circular node,
        // so we want the replacement div to be as wide as the input (88%)
        // rather than spanning the full circle, and to inherit the input's
        // centered text alignment.
        div.style.cssText = `
          font-family: ${cs.fontFamily};
          font-size: ${cs.fontSize};
          font-weight: ${cs.fontWeight};
          line-height: ${cs.lineHeight};
          letter-spacing: ${cs.letterSpacing};
          color: ${cs.color};
          text-align: ${cs.textAlign};
          padding: ${cs.padding};
          margin: ${cs.margin};
          ${cs.position !== 'static' ? `position: ${cs.position}; top: ${cs.top};` : ''}
          box-sizing: border-box;
          width: ${isFwLabel ? '88%' : '100%'};
          ${isTextarea ? 'white-space: pre-wrap; word-break: normal; overflow-wrap: break-word;' : (isFwLabel ? 'white-space: normal; overflow: hidden;' : 'white-space: nowrap; overflow: visible; clip-path: inset(0 0 -8px 0); text-overflow: clip;')}
          background: transparent;
          min-height: ${cs.minHeight};
          ${isDenseRock ? `height: ${cs.height}; overflow: hidden;` : ''}
        `
        const prevDisplay = input.style.display
        input.style.display = 'none'
        input.parentNode?.insertBefore(div, input)
        replacements.push({ input, div, prevDisplay })
      })
      await new Promise(r => requestAnimationFrame(() => r(null)))


      const LETTER_W = 8.5
      const LETTER_H = 11
      // Page is rendered at CSS width 816px (8.5in × 96dpi). To capture at exactly
      // 200dpi (matching the reference PDF's rasterisation), use scale ≈ 2.083 so
      // the canvas is 1700px wide. Lower scales force jsPDF to stretch on embed,
      // producing visible sub-pixel offsets in the rasterised output.
      const SCALE = 1700 / 816
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter', compress: true })

      for (let i = 0; i < 2; i++) {
        const el = i === 0 ? page1 : page2
        const canvas = await html2canvas(el, {
          scale: SCALE,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        })
        console.log('[svep-export] canvas page', i + 1, 'dims:', canvas.width, 'x', canvas.height, 'el dims:', el.offsetWidth, 'x', el.offsetHeight)
        const imgData = canvas.toDataURL('image/jpeg', 0.95)
        if (i > 0) pdf.addPage('letter', 'portrait')
        pdf.addImage(imgData, 'JPEG', 0, 0, LETTER_W, LETTER_H, undefined, 'FAST')
      }

      replacements.forEach(({ input, div, prevDisplay }) => {
        div.remove()
        input.style.display = prevDisplay
      })
      chromeReplacements.forEach(({ el, prevDisplay }) => {
        el.style.display = prevDisplay
      })
      container.classList.remove('svep-exporting')

      const safeName = team.name.replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '_')
      pdf.save(`SVEP_${safeName}_${current_quarter}.pdf`)
    } catch (err) {
      console.error('PDF export failed:', err)
      setExportError('PDF export failed. Please try again.')
      setTimeout(() => setExportError(null), 5000)
      const container = document.getElementById('svep-print-area')
      if (container) {
        container.classList.remove('svep-exporting')
        container.querySelectorAll('.svep-pdf-text').forEach(d => d.remove())
        container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach(i => { i.style.display = '' })
        container.querySelectorAll<HTMLElement>(
          '.svep-fw-controls, .svep-fw-logo-add, .svep-fw-logo-remove, .svep-fw-chooser, .svep-flywheel-hdr-actions, .svep-stepper, .svep-img-upload, .svep-img-remove'
        ).forEach(el => { el.style.display = '' })
      }
    } finally {
      setExporting(false)
    }
  }, [team.name, current_quarter])

  const autoSave = useCallback((type: 'annual' | 'quarterly', data: Record<string, any>) => {
    if (sessionExpired || !can_edit) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setSaveError(null)
    saveTimerRef.current = setTimeout(() => {
      const payload: Record<string, any> = {}
      payload[`${type}_data`] = data
      saveInFlightRef.current += 1
      router.patch(`/teams/${team.id}/svep?quarter=${encodeURIComponent(current_quarter)}`, payload, {
        preserveScroll: true,
        preserveState: true,
        only: [],
        onStart: () => setSaving(true),
        onSuccess: () => {
          setSaveError(null)
          setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
        },
        onError: (errors) => {
          let detail = 'Please try again.'
          if (errors && typeof errors === 'object') {
            const first = Object.values(errors)[0]
            if (typeof first === 'string' && first.length > 0) detail = first
          }
          setSaveError(`Failed to save: ${detail}`)
          setTimeout(() => setSaveError(prev => (prev && prev.startsWith('Failed to save:') ? null : prev)), 8000)
        },
        onFinish: () => {
          if (saveInFlightRef.current > 0) saveInFlightRef.current -= 1
          if (saveInFlightRef.current === 0) setSaving(false)
        },
      })
    }, 800)
  }, [team.id, current_quarter, sessionExpired, can_edit])

  const updateAnnual = (key: string, value: any) => {
    if (!can_edit) return
    const next = { ...annual, [key]: value }
    setAnnual(next)
    autoSave('annual', next)
  }

  const updateQuarterly = (key: string, value: any) => {
    if (!can_edit) return
    const next = { ...quarterlyRef.current, [key]: value }
    quarterlyRef.current = next
    setQuarterly(next)
    autoSave('quarterly', next)
  }

  const updateRockTitle = (id: number, value: string) => {
    updateQuarterly('rock_title_overrides', {
      ...(quarterlyRef.current.rock_title_overrides || {}),
      [String(id)]: value,
    })
  }

  const updateAnnualArray = (key: string, index: number, value: string) => {
    const arr = [...(annual[key] || [])]
    arr[index] = value
    updateAnnual(key, arr)
  }

  const updateCoreValue = (index: number, field: 'name' | 'description', value: string) => {
    const raw = annual.core_values || []
    const arr = raw.map((v: any) => (typeof v === 'string' ? { name: v, description: '' } : { name: v?.name || '', description: v?.description || '' }))
    while (arr.length <= index) arr.push({ name: '', description: '' })
    arr[index] = { ...arr[index], [field]: value }
    updateAnnual('core_values', arr)
  }

  const coreValueAt = (index: number): { name: string; description: string } => {
    const v = (annual.core_values || [])[index]
    if (!v) return { name: '', description: '' }
    if (typeof v === 'string') return { name: v, description: '' }
    return { name: v.name || '', description: v.description || '' }
  }

  const updateQuarterlyArray = (key: string, index: number, value: string) => {
    const arr = [...(quarterly[key] || [])]
    // Rails strips nils out of submitted arrays, so a hole before the edited
    // row would collapse and shift the text onto an earlier index. Rows filled
    // by a real Rock (task #701) are never stored, so those holes are normal —
    // always write empty strings instead of leaving the array sparse.
    for (let i = 0; i < index; i++) if (arr[i] == null) arr[i] = ''
    arr[index] = value
    updateQuarterly(key, arr)
  }

  const updateBrandPromise = (index: number, field: 'promise' | 'kpi', value: string) => {
    const arr = [...(quarterly.brand_promises || Array(5).fill({ promise: '', kpi: '' }))]
    arr[index] = { ...arr[index], [field]: value }
    updateQuarterly('brand_promises', arr)
  }

  const navigateQuarter = (q: string) => {
    router.get(`/teams/${team.id}/svep`, { quarter: q }, { preserveState: false })
  }

  const a = annual
  const q = quarterly
  // Rocks share a fixed area on the page; the team can show between 4 and 15 of
  // them and the rows re-flow to fill the space (see .svep-rocks-list flex).
  //
  // Task #701: the first rows are filled by the team's real Rock records for
  // the viewed quarter (read-only); the rest stay free-text bound to the same
  // stored array index they have always used. The stepper still controls how
  // many rows are drawn, but it can never hide a qualifying Rock, so the row
  // count is raised to the number of Rocks when the stored value is lower.
  const realRocks = team_rocks.slice(0, 15)
  const rocksMin = Math.max(4, realRocks.length)
  const rocksCount = Math.max(rocksMin, Math.min(15, q.rocks_count || 8))

  return (
    <Layout team={{ id: team.id, name: team.name, member_count: team_members.length }} activeTab={active_tab}>
      <Head title={`SVEP - ${team.name} - Pinnacle`} />

      <div className="svep-controls print:hidden">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-heading">The Strategic Vision & Execution Plan</h1>
            <p className="text-sm text-text-secondary mt-1">{team.name}</p>
          </div>
          <div className="flex items-center gap-3">
            {!can_edit && <span className="text-xs text-text-secondary font-medium">View only</span>}
            {exportError && <span className="text-xs text-[#C41920] font-medium">{exportError}</span>}
            {saving && <span className="text-xs text-text-muted">Saving...</span>}
            {!saving && saveError && <span className="text-xs text-[#ED1C24] font-semibold">{saveError}</span>}
            {!saving && !saveError && lastSaved && <span className="text-xs text-text-muted">Saved {lastSaved}</span>}
            <button onClick={exportPdf} disabled={exporting} className="px-4 py-2 border border-stone/30 rounded-lg text-sm text-text-secondary hover:bg-[#F8F9FA] font-heading flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {exporting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => navigateQuarter(prev_quarter)} className="p-2 rounded hover:bg-[#351E1D]/5">
            <svg className="w-4 h-4 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <select value={current_quarter} onChange={e => navigateQuarter(e.target.value)} className="px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 font-heading font-semibold">
            {available_quarters.map(qq => <option key={qq} value={qq}>{qq}</option>)}
          </select>
          <button onClick={() => navigateQuarter(next_quarter)} className="p-2 rounded hover:bg-[#351E1D]/5">
            <svg className="w-4 h-4 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      </div>

      <fieldset className="svep-pages border-0 p-0 min-w-0" id="svep-print-area" disabled={!can_edit}>

        {/* ═══════════ PAGE 1: ANNUAL / STRATEGIC VISION ═══════════ */}
        <div className="svep-page" id="svep-page-1">
          <SvepHeader />

          <div className="svep-row-3 svep-top-section">
            <div className="svep-col svep-col-border-r">
              <div className="svep-section-hdr svep-bg-black svep-hdr-left">
                <strong>Purpose:</strong> <span>Why We Exist?</span>
              </div>
              <div className="svep-cell svep-cell-purpose"><SvepField value={a.purpose || ''} onChange={v => updateAnnual('purpose', v)} multiline placeholder="Why does this organization exist?" /></div>
            </div>
            <div className="svep-col svep-col-border-r">
              <div className="svep-section-hdr svep-bg-gray svep-hdr-left">
                <strong>Pinnacle:</strong> <span>The Top of Our Mountain</span>
              </div>
              <div className="svep-cell svep-cell-purpose">
                <SvepImageField
                  value={a.pinnacle_image || null}
                  textValue={a.pinnacle || ''}
                  onImageChange={v => updateAnnual('pinnacle_image', v)}
                  onTextChange={v => updateAnnual('pinnacle', v)}
                  placeholder="What is the ultimate vision?"
                />
              </div>
            </div>
            <div className="svep-col">
              <div className="svep-section-hdr svep-bg-black"><strong>Annual Growth Plan</strong></div>
              <div className="svep-kv-rows">
                {[['Due Date', 'annual_due_date'], ['Profit', 'annual_profit'], ['Revenues', 'annual_revenues'], ['Key Metric', 'annual_key_metric_1'], ['Key Metric', 'annual_key_metric_2']].map(([label, key]) => (
                  <div key={key} className="svep-kv-row">
                    <span className="svep-kv-label">{label}</span>
                    <SvepField value={a[key as string] || ''} onChange={v => updateAnnual(key as string, v)} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="svep-row-3 svep-mid-section">
            <div className="svep-col svep-col-border-r svep-col-flex">
              <div className="svep-section-hdr svep-bg-gray"><strong>Core Business</strong></div>
              <div className="svep-cell svep-cell-corebiz"><SvepField value={a.core_business || ''} onChange={v => updateAnnual('core_business', v)} multiline placeholder="What is the core business?" /></div>
              <div className="svep-section-hdr svep-bg-black"><strong>Core Values</strong></div>
              <div className="svep-values-list">
                {[0,1,2,3,4,5].map(i => {
                  const cv = coreValueAt(i)
                  return (
                    <div key={i} className="svep-value-row">
                      <RedBullet />
                      <div className="svep-value-text">
                        <SvepField value={cv.name} onChange={v => updateCoreValue(i, 'name', v)} className="svep-value-name" placeholder="" />
                        <SvepField value={cv.description} onChange={v => updateCoreValue(i, 'description', v)} className="svep-value-desc" placeholder="" multiline />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="svep-col svep-col-border-r svep-col-flex">
              <div className="svep-section-hdr svep-bg-black"><strong>Milestones</strong></div>
              <div className="svep-kv-rows svep-kv-rows-bordered">
                {[['Future Date', 'milestones_future_date'], ['Profit', 'milestones_profit'], ['Revenues', 'milestones_revenues']].map(([label, key]) => (
                  <div key={key} className="svep-kv-row">
                    <span className="svep-kv-label">{label}</span>
                    <SvepField value={a[key as string] || ''} onChange={v => updateAnnual(key as string, v)} />
                  </div>
                ))}
              </div>
              <div className="svep-milestone-list">
                {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => {
                  const val = (a.milestone_items || [])[i] || ''
                  return (
                    <div key={i} className={`svep-milestone-row ${val ? '' : 'svep-milestone-empty'}`}><span className="svep-milestone-bullet">&bull;</span><SvepField value={val} onChange={v => updateAnnualArray('milestone_items', i, v)} /></div>
                  )
                })}
              </div>
            </div>
            <div className="svep-col svep-col-flex">
              <div className="svep-section-hdr svep-bg-gray"><strong>Goals</strong></div>
              <div className="svep-goals-list">
                {[1,2,3,4,5,6,7].map(n => (
                  <div key={n} className="svep-goal-row"><RedCircle n={n} /><SvepField value={(a.goals || [])[n-1] || ''} onChange={v => updateAnnualArray('goals', n-1, v)} multiline /></div>
                ))}
              </div>
            </div>
          </div>

          <div className="svep-trends">
            <div className="svep-trends-hdr">Trends</div>
            <div className="svep-trends-grid">
              {[0,1,2,3,4,5].map(i => (
                <div key={i} className="svep-trend-row">
                  <span className="svep-trend-num">{i + 1}.</span>
                  <SvepField value={(a.trends || [])[i] || ''} onChange={v => updateAnnualArray('trends', i, v)} />
                </div>
              ))}
            </div>
          </div>

          <PinnacleFooter withText />
        </div>

        {/* ═══════════ PAGE 2: QUARTERLY EXECUTION ═══════════ */}
        <div className="svep-page svep-page-2" id="svep-page-2">
          <SvepHeader />

          <div className="svep-row-2 svep-q-body">
            <div className="svep-col svep-col-border-r svep-col-flex">
              <div className="svep-section-hdr svep-bg-darkgray"><strong>Quarterly Execution</strong></div>
              <div className="svep-kv-rows svep-kv-rows-bordered">
                {[['Due Date', 'quarterly_due_date'], ['Profit', 'quarterly_profit'], ['Revenues', 'quarterly_revenues'], ['Key Metric', 'quarterly_key_metric']].map(([label, key]) => (
                  <div key={key} className="svep-kv-row">
                    <span className="svep-kv-label">{label}</span>
                    <SvepField value={q[key as string] || ''} onChange={v => updateQuarterly(key as string, v)} />
                  </div>
                ))}
              </div>
              <div className="svep-section-hdr svep-bg-black svep-hdr-big">
                <strong>Rocks</strong>
                <CountStepper
                  label="Rocks"
                  value={rocksCount}
                  min={rocksMin}
                  max={15}
                  onChange={c => updateQuarterly('rocks_count', Math.max(rocksMin, Math.min(15, c)))}
                />
              </div>
              <div className={`svep-rocks-list${rocksCount > 8 ? ' svep-rocks-dense' : ''}`}>
                {Array.from({ length: rocksCount }, (_, i) => i + 1).map(n => {
                  const rock = realRocks[n-1]
                  const overrides = q.rock_title_overrides || {}
                  const value = rock
                    ? (Object.prototype.hasOwnProperty.call(overrides, String(rock.id)) ? overrides[String(rock.id)] : rock.title)
                    : ((q.rocks || [])[n-1] || '')
                  return (
                    <div key={rock ? `rock-${rock.id}` : `free-${n}`} className={`svep-rock-row${rock ? ' svep-rock-row-linked' : ''}`}
                      data-rock-id={rock ? rock.id : undefined}>
                      <RedCircle n={n} />
                      {rock
                        ? <LinkedRockField value={value} owner={rock.owner_name} readOnly={!can_edit} count={rocksCount}
                            onChange={v => updateRockTitle(rock.id, v)} />
                        : rocksCount > 8
                        ? <DenseRockField value={value} readOnly={!can_edit} count={rocksCount}
                            onChange={v => updateQuarterlyArray('rocks', n-1, v)} />
                        : <SvepField value={value} readOnly={!can_edit} multiline
                            onChange={v => updateQuarterlyArray('rocks', n-1, v)} />
                      }
                    </div>
                  )
                })}
              </div>
              <div className="svep-strengths">
                <div className="svep-red-label">Strengths:</div>
                {[0,1,2].map(i => (
                  <div key={i} className="svep-numbered-row"><span>{i + 1}.</span><SvepField value={(q.strengths || [])[i] || ''} onChange={v => updateQuarterlyArray('strengths', i, v)} /></div>
                ))}
              </div>
            </div>

            <div className="svep-col svep-col-flex">
              <div className="svep-section-hdr svep-bg-black"><strong>Quarterly Theme</strong></div>
              <div className="svep-theme-name">
                <strong>Theme Name</strong>
                <SvepField value={q.theme_name || ''} onChange={v => updateQuarterly('theme_name', v)} placeholder="Enter quarterly theme name" />
              </div>
              <div className="svep-theme-reward">
                <span>Celebration / Reward:</span>
                <SvepField value={q.celebration || ''} onChange={v => updateQuarterly('celebration', v)} />
              </div>
              <div className="svep-flywheel">
                <SvepFlywheelField
                  headerTitle="Flywheel Design"
                  imageValue={q.flywheel_image || null}
                  builderValue={q.flywheel_builder || null}
                  textValue={q.flywheel_design || ''}
                  onImageChange={v => {
                    if (v && q.flywheel_builder) {
                      const next = { ...quarterly, flywheel_image: v, flywheel_builder: null }
                      setQuarterly(next)
                      autoSave('quarterly', next)
                    } else {
                      updateQuarterly('flywheel_image', v)
                    }
                  }}
                  onBuilderChange={v => {
                    if (v && q.flywheel_image) {
                      const next = { ...quarterly, flywheel_builder: v, flywheel_image: null }
                      setQuarterly(next)
                      autoSave('quarterly', next)
                    } else {
                      updateQuarterly('flywheel_builder', v)
                    }
                  }}
                  onTextChange={v => updateQuarterly('flywheel_design', v)}
                  placeholder=""
                />
              </div>
              <div className="svep-one-phrase">
                <strong>One-Phrase Strategy</strong>
                <SvepField value={q.one_phrase_strategy || ''} onChange={v => updateQuarterly('one_phrase_strategy', v)} />
              </div>
              <div className="svep-brand">
                <div className="svep-brand-hdr">
                  <span className="svep-brand-hdr-left">Brand Promises</span>
                  <span className="svep-brand-hdr-right">Brand Promise KPIs</span>
                </div>
                {[0,1,2,3,4].map(i => (
                  <div key={i} className={`svep-brand-row ${i % 2 === 0 ? 'svep-brand-row-alt' : ''}`}>
                    <div className="svep-brand-left"><DarkCircle n={i + 1} /><SvepField value={(q.brand_promises || [])[i]?.promise || ''} onChange={v => updateBrandPromise(i, 'promise', v)} /></div>
                    <div className="svep-brand-right"><SvepField value={(q.brand_promises || [])[i]?.kpi || ''} onChange={v => updateBrandPromise(i, 'kpi', v)} /></div>
                  </div>
                ))}
              </div>
              <div className="svep-sayno">
                <div className="svep-red-label">Things To Say No To:</div>
                {[0,1,2].map(i => (
                  <div key={i} className="svep-numbered-row"><span>{i + 1}.</span><SvepField value={(q.things_to_say_no_to || [])[i] || ''} onChange={v => updateQuarterlyArray('things_to_say_no_to', i, v)} /></div>
                ))}
              </div>
            </div>
          </div>

          <PinnacleFooter withText />
        </div>
      </fieldset>
    </Layout>
  )
}
